import * as React from "react";
import { Card, CardBody, CardHeader } from "../components/Card";
import { SectionHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { Field, Select } from "../components/Form";
import { Badge, StatusBadge } from "../components/Badge";
import { Banner } from "../components/States";
import { DetailField, DetailSection } from "../components/Detail";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { ProcedureModel, ProcedureVersionModel } from "../api/APIClient";
import { describeErrorWithFindings, severityOf } from "../api/errors";
import * as Configuration from "../configuration/ConfigurationStore";
import * as Structure from "../structure/StructureStore";
import { listGradeScales } from "../grading/GradeScaleStore";
import { FindingList, FindingSummary } from "./FindingList";
import { GroupPathEditor, Option, RuleEditor } from "./RuleEditor";
import {
    CALCULATION_METHODS,
    METHOD_HINTS,
    METHOD_LABELS,
    MISSING_MARK_POLICIES,
    POLICY_HINTS,
    POLICY_LABELS,
    ProcedureDraft,
    TARGET_SELECTOR_KINDS,
    TARGET_SELECTOR_LABELS,
    emptyDraft,
    emptyRule,
    toDraft,
} from "./vocabulary";
import * as Store from "./ProceduresStore";

/**
 * Authoring one procedure's draft version.
 *
 * The lifecycle is deliberately staged: save a draft, validate it, publish it, then assign the
 * published version to a module. Publishing needs its own permission because a published version is
 * immutable - changing it means copying it into a new draft and publishing again, which is what
 * keeps historical marks explainable.
 *
 * This was previously one region of a 1,173-line component that also owned mark-type creation,
 * assignment selection and running calculations. Those are now their own screens.
 */

export interface ProcedureEditorProps {
    procedure: ProcedureModel;
    onChanged: () => void;
}

export const ProcedureEditor: React.FC<ProcedureEditorProps> = ({ procedure, onChanged }) => {
    const toast = useToast();
    const canWrite = usePermission(Permissions.PROCEDURES_WRITE);
    const canPublish = usePermission(Permissions.PROCEDURES_PUBLISH);

    const procedureID = procedure.id ?? "";

    const versions = useAsync(() => Store.listVersions(procedureID), [procedureID], { initial: [] });
    const markTypes = useAsync(() => Configuration.listMarkTypes(), [], { initial: [] });
    const assessmentTypes = useAsync(() => Configuration.listAssessmentTypes(), [], { initial: [] });
    const groupTypes = useAsync(() => Configuration.listGroupTypes(), [], { initial: [] });
    const modules = useAsync(() => Structure.listModules(), [], { initial: [] });
    const gradeScales = useAsync(() => listGradeScales(), [], { initial: [] });

    const draftVersion = (versions.data ?? []).find((v) => v.status === "Draft");
    const published = (versions.data ?? []).filter((v) => v.status === "Published").sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0));

    const loadedDraft = useAsync<ProcedureVersionModel | undefined>(
        () => (draftVersion?.id ? Store.getVersion(draftVersion.id) : Promise.resolve(undefined)),
        [draftVersion?.id],
        { enabled: !!draftVersion?.id }
    );

    const [draft, setDraft] = React.useState<ProcedureDraft>(emptyDraft);
    const [savedDraft, setSavedDraft] = React.useState<ProcedureDraft>(emptyDraft);
    const [findings, setFindings] = React.useState<ProcedureVersionModel["findings"]>([]);

    const acceptDraft = React.useCallback((version?: ProcedureVersionModel) => {
        const next = toDraft(version);
        setDraft(next);
        setSavedDraft(next);
        setFindings(version?.findings ?? []);
    }, []);

    React.useEffect(() => {
        acceptDraft(loadedDraft.data);
    }, [loadedDraft.data, acceptDraft]);

    const opts = (items?: Array<{ id?: string; name?: string }>): Option[] =>
        (items ?? []).map((i) => ({ value: i.id ?? "", label: i.name ?? "(unnamed)" }));

    const markTypeOptions = React.useMemo(() => opts(markTypes.data), [markTypes.data]);
    const groupTypeOptions = React.useMemo(() => opts(groupTypes.data), [groupTypes.data]);

    const save = useAction(async () => {
        const saved = await Store.saveDraft(procedureID, draft);
        acceptDraft(saved);
        toast.success("Draft saved.");
        versions.refresh();
    }, describeErrorWithFindings);

    const validate = useAction(async () => {
        if (canWrite) {
            const saved = await Store.saveDraft(procedureID, draft);
            acceptDraft(saved);
        }
        const validated = await Store.validateDraft(procedureID);
        setFindings(validated.findings ?? []);
        const errors = (validated.findings ?? []).filter((f) => severityOf(f) === "Error");
        if (errors.length) toast.warning(`${errors.length} error(s) would block publishing.`);
        else toast.success("Draft validates cleanly.");
    }, describeErrorWithFindings);

    const publish = useAction(async () => {
        const version = await Store.publishDraft(procedureID);
        toast.success(`Published version ${version.versionNumber}.`);
        versions.refresh();
        onChanged();
    }, describeErrorWithFindings);

    const discard = useAction(async () => {
        await Store.discardDraft(procedureID);
        toast.success("Draft discarded.");
        acceptDraft();
        versions.refresh();
    });

    const startDraftFrom = useAction(async () => {
        const source = published[0];
        if (!source?.id) return;
        await Store.copyVersionToDraft(procedureID, source.id);
        toast.success(`Draft started from version ${source.versionNumber}.`);
        versions.refresh();
    });

    const hasDraft = !!draftVersion;
    const hasUnsavedChanges = JSON.stringify(draft) !== JSON.stringify(savedDraft);
    const busy = save.busy || validate.busy || publish.busy || discard.busy || startDraftFrom.busy;
    const loadingDraft = versions.isLoading || loadedDraft.isLoading || (!!draftVersion && loadedDraft.data?.id !== draftVersion.id);
    const readOnly = !canWrite || busy || loadingDraft;
    const currentFindings = hasUnsavedChanges ? [] : findings;
    const set = (patch: Partial<ProcedureDraft>) => {
        setDraft((d) => ({ ...d, ...patch }));
        save.clearError();
        validate.clearError();
        publish.clearError();
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <SectionHeader
                        title="Versions"
                        description="A published version is frozen. To change one, start a new draft from it."
                        actions={
                            canWrite && !hasDraft && published.length > 0 ? (
                                <Button size="sm" variant="outline-primary" icon={["fad", "copy"]} onClick={startDraftFrom.run} loading={startDraftFrom.busy} disabled={busy || loadingDraft}>
                                    New draft from v{published[0].versionNumber}
                                </Button>
                            ) : undefined
                        }
                    />
                </CardHeader>
                <CardBody>
                    <div className="flex flex-wrap gap-2">
                        {(versions.data ?? []).length === 0 && <p className="text-sm text-text-weak">No versions yet.</p>}
                        {(versions.data ?? []).map((v) => (
                            <Badge key={v.id} tone={v.status === "Draft" ? "warning" : "success"}>
                                {v.status === "Draft" ? "Draft" : `v${v.versionNumber}`}
                            </Badge>
                        ))}
                    </div>
                    {startDraftFrom.error && <Banner tone="danger" className="mt-3">{startDraftFrom.error}</Banner>}
                </CardBody>
            </Card>

            {!hasDraft && (
                <Card>
                    <CardBody>
                        <Banner tone="info">
                            This procedure has no open draft. {published.length ? "Start one from a published version to make changes." : "Save a draft below to create the first version."}
                        </Banner>
                    </CardBody>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <SectionHeader title="What this procedure produces" description="The mark it writes, and how it combines its inputs." />
                </CardHeader>
                <CardBody>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Produces which mark" required hint="A chained procedure needs one mark type per level - entered, division, term.">
                            <Select
                                value={draft.targetMarkTypeID}
                                placeholder="Select a mark type"
                                disabled={readOnly}
                                onChange={(e) => set({ targetMarkTypeID: e.target.value })}
                            >
                                {markTypeOptions.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="Grade scale override" hint="Leave empty to use the mark type's default scale.">
                            <Select
                                value={draft.gradeScaleID ?? ""}
                                placeholder="Use the mark type default"
                                disabled={readOnly}
                                onChange={(e) => set({ gradeScaleID: e.target.value || undefined })}
                            >
                                {(gradeScales.data ?? []).map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="Method" required hint={METHOD_HINTS[draft.calculationMethod]}>
                            <Select
                                value={draft.calculationMethod}
                                disabled={readOnly}
                                onChange={(e) => set({ calculationMethod: e.target.value as ProcedureDraft["calculationMethod"] })}
                            >
                                {CALCULATION_METHODS.map((m) => (
                                    <option key={m} value={m}>
                                        {METHOD_LABELS[m]}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="When an input is missing" required hint={POLICY_HINTS[draft.missingMarkPolicy]}>
                            <Select
                                value={draft.missingMarkPolicy}
                                disabled={readOnly}
                                onChange={(e) => set({ missingMarkPolicy: e.target.value as ProcedureDraft["missingMarkPolicy"] })}
                            >
                                {MISSING_MARK_POLICIES.map((p) => (
                                    <option key={p} value={p}>
                                        {POLICY_LABELS[p]}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                    </div>
                </CardBody>
            </Card>

            <Card>
                <CardHeader>
                    <SectionHeader title="Where the mark is written" description="The target the engine produces a result against." />
                </CardHeader>
                <CardBody>
                    <Field label="Target" required>
                        <Select
                            value={draft.targetSelectorKind}
                            disabled={readOnly}
                            onChange={(e) =>
                                set({ targetSelectorKind: e.target.value as ProcedureDraft["targetSelectorKind"], targetPath: [] })
                            }
                        >
                            {TARGET_SELECTOR_KINDS.map((k) => (
                                <option key={k} value={k}>
                                    {TARGET_SELECTOR_LABELS[k]}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    {draft.targetSelectorKind === "AssessmentGroupPath" && (
                        <GroupPathEditor
                            label="Path to the target group"
                            path={draft.targetPath}
                            groupTypes={groupTypeOptions}
                            disabled={readOnly}
                            onChange={(targetPath) => set({ targetPath })}
                        />
                    )}
                </CardBody>
            </Card>

            <Card>
                <CardHeader>
                    <SectionHeader
                        title="Inputs"
                        description="Each rule contributes marks to the calculation."
                        actions={
                            canWrite && (
                                <Button size="sm" variant="outline-primary" icon={["fad", "plus"]} disabled={readOnly} onClick={() => set({ rules: [...draft.rules, emptyRule()] })}>
                                    Add rule
                                </Button>
                            )
                        }
                    />
                </CardHeader>
                <CardBody>
                    {draft.rules.length === 0 && (
                        <Banner tone="warning">A procedure with no rules has nothing to calculate from and will not publish.</Banner>
                    )}

                    <div className="space-y-3">
                        {draft.rules.map((rule, index) => (
                            <RuleEditor
                                key={rule.id ?? index}
                                rule={rule}
                                index={index}
                                showWeight={draft.calculationMethod === "WeightedAverage"}
                                markTypes={markTypeOptions}
                                assessmentTypes={opts(assessmentTypes.data)}
                                groupTypes={groupTypeOptions}
                                modules={opts(modules.data)}
                                disabled={readOnly}
                                onChange={(patch) => set({ rules: draft.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)) })}
                                onRemove={() => set({ rules: draft.rules.filter((_, i) => i !== index) })}
                            />
                        ))}
                    </div>
                </CardBody>
            </Card>

            <Card>
                <CardBody>
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <Button icon={["fad", "floppy-disk"]} onClick={save.run} loading={save.busy} disabled={readOnly}>
                            Save draft
                        </Button>
                        <Button variant="outline-secondary" icon={["fad", "clipboard-check"]} onClick={validate.run} loading={validate.busy} disabled={!hasDraft || busy || loadingDraft}>
                            {canWrite ? "Save and validate" : "Validate"}
                        </Button>
                        <Button
                            variant="outline-success"
                            icon={["fad", "circle-check"]}
                            onClick={publish.run}
                            loading={publish.busy}
                            disabled={!hasDraft || !canPublish || hasUnsavedChanges || busy || loadingDraft}
                            title={!canPublish ? "Publishing needs the procedures.publish permission." : hasUnsavedChanges ? "Save and validate your changes before publishing." : undefined}
                        >
                            Publish
                        </Button>
                        <Button variant="ghost" icon={["fad", "trash"]} onClick={discard.run} loading={discard.busy} disabled={!hasDraft || readOnly}>
                            Discard draft
                        </Button>

                        <div className="ml-auto">
                            <FindingSummary findings={currentFindings as any} />
                        </div>
                    </div>

                    {[save, validate, publish, discard].map((a, i) =>
                        a.error ? (
                            <Banner key={i} tone="danger" className="mb-2">
                                {a.error}
                            </Banner>
                        ) : null
                    )}

                    {hasUnsavedChanges ? (
                        <Banner tone="info">Changes have not been validated. Save and validate to check the current selections.</Banner>
                    ) : (
                        <FindingList findings={currentFindings as any} emptyMessage="No findings. Validate the draft to check it against the rules." />
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

/** Read-only summary of a published version, for the versions list. */
export const VersionSummary: React.FC<{ version: ProcedureVersionModel }> = ({ version }) => (
    <DetailSection title={`Version ${version.versionNumber ?? ""}`}>
        <DetailField label="Status">
            <StatusBadge status={version.status} />
        </DetailField>
        <DetailField label="Produces">{version.targetMarkType?.name}</DetailField>
        <DetailField label="Method">{METHOD_LABELS[version.calculationMethod as keyof typeof METHOD_LABELS] ?? version.calculationMethod}</DetailField>
        <DetailField label="Missing marks">
            {POLICY_LABELS[version.missingMarkPolicy as keyof typeof POLICY_LABELS] ?? version.missingMarkPolicy}
        </DetailField>
        <DetailField label="Grade scale">{version.gradeScale?.name}</DetailField>
        <DetailField label="Rules">{version.rules?.length ?? 0}</DetailField>
    </DetailSection>
);

export default ProcedureEditor;
